import React, { createContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { ConnectionInfo, ConnectionKind, ConnectionsClient } from "Services/API/API";
import { useApiClient } from "Services/API/useApiClient";
import { defaultContextPromiseFunction } from "Utils/defaultContextFunction";
import { userSelector } from "Utils/selectors";

export interface IConnectionsContext {
   connections: ConnectionInfo[];
   isLoading: boolean;
   load: (signal?: AbortSignal) => Promise<void>;
   follow: (targetId: Guid) => Promise<void>;
   unfollow: (targetId: Guid) => Promise<void>;
}

export const ConnectionsContext = createContext<IConnectionsContext>({
   connections: [],
   isLoading: false,
   load: defaultContextPromiseFunction,
   follow: defaultContextPromiseFunction,
   unfollow: defaultContextPromiseFunction,
});

interface ConnectionsContextProviderProps {
   preloadConnections?: boolean
}

export const ConnectionsContextProvider: React.FC<ConnectionsContextProviderProps> = ({ children, preloadConnections }) => {
   const connectionsClient = useApiClient(ConnectionsClient);
   const user = useSelector(userSelector);
   const [connections, setConnections] = useState<ConnectionInfo[]>([]);
   const [isLoading, setIsLoading] = useState(false);
   const load = async (signal?: AbortSignal) => {
      if (user.isAuthenticated) {
         setIsLoading(true);
         try
         {
            setConnections(await connectionsClient.list(ConnectionKind.Following, signal));
         }
         finally
         {
            setIsLoading(false);
         }
      }
   }

   const follow = async (targetId: Guid) => {
      try {
         await connectionsClient.follow(targetId);
      }
      finally {
         await load();
      }
   }

   const unfollow = async (targetId: Guid) => {
      try {
         await connectionsClient.unfollow(targetId);
      }
      finally {
         await load();
      }
   }

   useEffect(() => {
      const abortController = new AbortController();
      if(preloadConnections) {         
         load(abortController.signal);
      }

      return () => {
         abortController.abort();
      }
   }, [preloadConnections])

   return (<ConnectionsContext.Provider value={{
      connections,      
      isLoading,
      load,
      follow,
      unfollow,
   }}>
      {children}
   </ConnectionsContext.Provider>);
}